export function CommonRemodelMistakes() {
  const mistakes = [
    {
      icon: 'ri-funds-line',
      title: 'Over-Improving for the Market',
      description: 'Installing luxury finishes in a working-class neighborhood where tenants can\'t afford premium rents.',
      consequence: 'You spend $50,000 on granite and stainless appliances but can only raise rents by $50/month',
      solution: 'Sell AS IS to investors who understand the market and will make appropriate improvements'
    },
    {
      icon: 'ri-calculator-line',
      title: 'Underestimating Total Costs',
      description: 'Focusing only on direct renovation costs while ignoring carrying costs, lost income, and hidden expenses.',
      consequence: 'Your $500,000 renovation actually costs $1.2M when you factor in all the hidden costs',
      solution: 'Avoid the entire cost burden by selling AS IS and letting the buyer handle renovations'
    },
    {
      icon: 'ri-time-line',
      title: 'Unrealistic Timeline Expectations',
      description: 'Assuming renovations will take 6 months when reality is 12-18 months with delays and issues.',
      consequence: 'Extended carrying costs and lost income drain your profits for an extra year',
      solution: 'Close in 7-30 days with an AS IS sale instead of waiting 12-18 months'
    },
    {
      icon: 'ri-team-line',
      title: 'Hiring the Wrong Contractors',
      description: 'Choosing the lowest bid without vetting experience, leading to poor work quality and delays.',
      consequence: 'Contractor abandons project mid-way or delivers substandard work requiring expensive fixes',
      solution: 'Transfer all contractor risk to professional investors who have established teams'
    },
    {
      icon: 'ri-file-list-line',
      title: 'Ignoring Permit Requirements',
      description: 'Starting work without proper permits to save time and money, creating legal and financial problems.',
      consequence: 'City stops work, requires expensive corrections, and delays project by months',
      solution: 'Sell AS IS and avoid all permit headaches and compliance issues'
    },
    {
      icon: 'ri-group-line',
      title: 'Not Considering Tenant Impact',
      description: 'Failing to account for tenant relocation costs, vacancy losses, and lease-up challenges.',
      consequence: 'Lose $200,000+ in rental income during renovation and struggle to find new tenants',
      solution: 'Keep tenants in place and transfer the property AS IS with existing leases'
    },
    {
      icon: 'ri-money-dollar-circle-line',
      title: 'Depleting Cash Reserves',
      description: 'Using all available capital for renovations, leaving no cushion for emergencies or opportunities.',
      consequence: 'Unable to handle unexpected costs or take advantage of other investment opportunities',
      solution: 'Preserve your capital by selling AS IS and reinvesting in better opportunities'
    },
    {
      icon: 'ri-line-chart-line',
      title: 'Ignoring Market Cap Rates',
      description: 'Assuming renovation costs will translate directly to increased property value.',
      consequence: 'Spend $1M on renovations but property value only increases by $200,000',
      solution: 'Understand that investors value income, not improvements - sell AS IS at fair market value'
    },
    {
      icon: 'ri-emotion-unhappy-line',
      title: 'Underestimating Stress & Time',
      description: 'Not accounting for the mental and emotional toll of managing a major renovation project.',
      consequence: 'Months of stress, sleepless nights, and constant problem-solving affecting health and relationships',
      solution: 'Eliminate all stress by selling AS IS and moving on with your life immediately'
    }
  ];

  return (
    <section className="py-20 bg-gradient-to-br from-red-50 to-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            9 Costly Mistakes Apartment Owners Make When Remodeling
          </h2>
          <p className="text-xl text-gray-700 max-w-3xl mx-auto">
            Learn from the expensive mistakes others have made. These common errors can turn a renovation project into a financial disaster.
          </p>
        </div>

        {/* Mistakes Grid */}
        <div className="grid md:grid-cols-3 gap-8 mb-16">
          {mistakes.map((mistake, index) => (
            <div key={index} className="bg-white rounded-2xl p-8 shadow-lg border-2 border-red-200">
              <div className="w-14 h-14 bg-red-600 rounded-xl flex items-center justify-center mb-6">
                <i className={`${mistake.icon} text-2xl text-white`}></i>
              </div>
              <h3 className="text-xl font-bold mb-4">{mistake.title}</h3>
              <div className="space-y-4">
                <div>
                  <div className="text-sm font-bold text-red-600 mb-2">The Mistake:</div>
                  <p className="text-gray-700 text-sm">{mistake.description}</p>
                </div>
                <div className="bg-red-50 rounded-lg p-4 border border-red-200">
                  <div className="text-sm font-bold text-red-700 mb-2">The Consequence:</div>
                  <p className="text-gray-700 text-sm">{mistake.consequence}</p>
                </div>
                <div className="bg-teal-50 rounded-lg p-4 border border-teal-200">
                  <div className="text-sm font-bold text-teal-700 mb-2">The Solution:</div>
                  <p className="text-gray-700 text-sm">{mistake.solution}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Warning Box */}
        <div className="bg-gradient-to-br from-red-100 to-red-50 rounded-2xl p-10 border-2 border-red-300 mb-12">
          <div className="flex items-start gap-6">
            <div className="w-16 h-16 bg-red-600 rounded-xl flex items-center justify-center flex-shrink-0">
              <i className="ri-alert-line text-3xl text-white"></i>
            </div>
            <div>
              <h3 className="text-3xl font-bold mb-4">Don't Become Another Statistic</h3>
              <p className="text-xl text-gray-800 mb-6 leading-relaxed">
                We've seen countless apartment building owners make these mistakes, losing hundreds of thousands of dollars in the process. The good news? You can avoid all of them by choosing to sell AS IS.
              </p>
              <p className="text-lg text-gray-700">
                When you sell AS IS to experienced investors like us, you transfer all renovation risk, avoid all these costly mistakes, and walk away with cash in hand. It's the smart, stress-free choice.
              </p>
            </div>
          </div>
        </div>

        {/* Image */}
        <div className="rounded-2xl overflow-hidden shadow-xl">
          <img
            src="https://public.readdy.ai/ai/img_res/b7a77863f78673ad6161e1dd0c9cd636.jpg"
            alt="Common apartment renovation mistakes"
            className="w-full h-[500px] object-cover object-top"
          />
        </div>
      </div>
    </section>
  );
}